import apiV2 from '../api/v2';
import type {
    TimelineEvent,
    TimelineListResponse
} from '../components/patient-files/actions/types';

type TimelineStatus = 'active' | 'draft' | 'invalidated' | 'pending_review';

interface ListTimelineParams {
    type?: string;
    /**
     * Optional `isLatest` filter:
     *   - omitted   → backend returns full version history across all groups
     *   - `true`    → only head row per group
     *   - `false`   → only non-head rows
     */
    isLatest?: boolean;
    page?: number;
    limit?: number;
    status?: TimelineStatus | TimelineStatus[];
}

export async function fetchTimeline(
    patientId: string,
    params: ListTimelineParams = {}
): Promise<TimelineListResponse> {
    const response = await apiV2.get<{ success: boolean; data: TimelineListResponse }>(
        `/patients/${patientId}/timeline`,
        { params }
    );
    return response.data.data;
}

/** One continuous stay in a workflow status, as replayed by the backend. */
export interface JourneySegment {
    status: string;
    macro: string | null;
    /** Display label for the phase (e.g. 'Design', 'Done'); null when unknown. */
    phase: string | null;
    /** ISO timestamp; null when the segment predates the timeline log. */
    enteredAt: string | null;
    /** ISO timestamp; null while this is the patient's current status. */
    leftAt: string | null;
    /** Milliseconds spent in the segment; null when enteredAt is unknown. */
    durationMs: number | null;
    /** Action that moved the patient into this segment; null for the first. */
    viaAction: string | null;
}

export async function fetchJourney(patientId: string): Promise<JourneySegment[]> {
    const response = await apiV2.get<{ success: boolean; data: JourneySegment[] }>(
        `/patients/${patientId}/timeline/journey`
    );
    return response.data.data;
}

export async function fetchTimelineEvent(
    patientId: string,
    eventId: string
): Promise<TimelineEvent> {
    const response = await apiV2.get<{ success: boolean; data: TimelineEvent }>(
        `/patients/${patientId}/timeline/${eventId}`
    );
    return response.data.data;
}

export async function fetchVersionHistory(
    patientId: string,
    groupId: string
): Promise<TimelineEvent[]> {
    const response = await apiV2.get<{ success: boolean; data: TimelineEvent[] }>(
        `/patients/${patientId}/timeline/groups/${groupId}/versions`
    );
    return response.data.data;
}

export async function createTimelineEvent(
    patientId: string,
    body: { type: string; visibility?: string; payload: Record<string, unknown>; groupId?: string }
): Promise<TimelineEvent> {
    const response = await apiV2.post<{ success: boolean; data: TimelineEvent }>(
        `/patients/${patientId}/timeline`,
        body
    );
    return response.data.data;
}

export async function createTimelineVersion(
    patientId: string,
    eventId: string,
    body: { visibility?: string; payload: Record<string, unknown> }
): Promise<TimelineEvent> {
    const response = await apiV2.put<{ success: boolean; data: TimelineEvent }>(
        `/patients/${patientId}/timeline/${eventId}`,
        body
    );
    return response.data.data;
}

export async function invalidateTimelineEvent(
    patientId: string,
    eventId: string,
    reason: string | null
): Promise<TimelineEvent> {
    const response = await apiV2.patch<{ success: boolean; data: TimelineEvent }>(
        `/patients/${patientId}/timeline/${eventId}/invalidate`,
        { reason }
    );
    return response.data.data;
}
